import { ChangeState, Contract, ContractWithPeriod, Period, PeriodIssueState, SignedUrl } from '@monorepo/interfaces';
import { Action, Selector, State } from '@ngxs/store';
import type { StateContext } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import { FleetManagementService } from '../services/fleet-management.service';
import {
    ClearDownloadLink,
    DeleteAllPeriods,
    DownloadBrowseContracts,
    LoadBrowseContracts,
    LoadContracts,
    LoadSelectedPeriod,
    LoadWarningContracts,
    MovePeriodBackward,
    SubmitReview
} from './fleet-management.actions';
import { finalize, Observable, tap } from 'rxjs';
import { map } from 'rxjs/operators';

export interface FleetManagementStateModel {
    contracts: ContractWithPeriod[];
    keepContracts: Contract[];
    updateContracts: Contract[];
    deleteContracts: Contract[];
    createContracts: Contract[];
    invalidContracts: Contract[];
    warningContracts: Contract[];
    isLoadingPeriods: boolean;
    isLoadingAllContracts: boolean;
    isLoadingWarningContracts: boolean;
    isDownloadingContracts: boolean;
    downloadLink?: string;
    totalItemCount: number;
    keepTotalItemCount: number;
    updateTotalItemCount: number;
    deleteTotalItemCount: number;
    createTotalItemCount: number;
    invalidTotalItemCount: number;
    warningTotalItemCount: number;
    selectedPeriod?: Period;
    loadingStates: Record<ChangeState, boolean>;
}

@State<FleetManagementStateModel>({
    name: 'fleetManagement',
    defaults: {
        contracts: [],
        keepContracts: [],
        updateContracts: [],
        deleteContracts: [],
        createContracts: [],
        invalidContracts: [],
        warningContracts: [],
        isLoadingPeriods: false,
        isLoadingAllContracts: false,
        isLoadingWarningContracts: false,
        isDownloadingContracts: false,
        totalItemCount: 0,
        keepTotalItemCount: 0,
        updateTotalItemCount: 0,
        deleteTotalItemCount: 0,
        createTotalItemCount: 0,
        invalidTotalItemCount: 0,
        warningTotalItemCount: 0,
        loadingStates: {
            [ChangeState.INVALID]: false,
            [ChangeState.DELETE]: false,
            [ChangeState.UPDATE]: false,
            [ChangeState.CREATE]: false,
            [ChangeState.KEEP]: false
        }
    }
})
@Injectable()
export class FleetManagementState {
    private fleetManagementService = inject(FleetManagementService);

    @Selector()
    static contracts(state: FleetManagementStateModel): ContractWithPeriod[] {
        return state.contracts;
    }

    @Selector()
    static keepContracts(state: FleetManagementStateModel): Contract[] {
        return state.keepContracts;
    }

    @Selector()
    static updateContracts(state: FleetManagementStateModel): Contract[] {
        return state.updateContracts;
    }

    @Selector()
    static deleteContracts(state: FleetManagementStateModel): Contract[] {
        return state.deleteContracts;
    }

    @Selector()
    static createContracts(state: FleetManagementStateModel): Contract[] {
        return state.createContracts;
    }

    @Selector()
    static invalidContracts(state: FleetManagementStateModel): Contract[] {
        return state.invalidContracts;
    }

    @Selector()
    static totalItemCount(state: FleetManagementStateModel): number {
        return state.totalItemCount;
    }

    @Selector()
    static keepTotalItemCount(state: FleetManagementStateModel): number {
        return state.keepTotalItemCount;
    }

    @Selector()
    static updateTotalItemCount(state: FleetManagementStateModel): number {
        return state.updateTotalItemCount;
    }

    @Selector()
    static deleteTotalItemCount(state: FleetManagementStateModel): number {
        return state.deleteTotalItemCount;
    }

    @Selector()
    static createTotalItemCount(state: FleetManagementStateModel): number {
        return state.createTotalItemCount;
    }

    @Selector()
    static invalidTotalItemCount(state: FleetManagementStateModel): number {
        return state.invalidTotalItemCount;
    }

    @Selector()
    static warningContracts(state: FleetManagementStateModel): Contract[] {
        return state.warningContracts;
    }

    @Selector()
    static warningTotalItemCount(state: FleetManagementStateModel): number {
        return state.warningTotalItemCount;
    }

    @Selector()
    static isLoadingWarningContracts(state: FleetManagementStateModel): boolean {
        return state.isLoadingWarningContracts;
    }

    @Selector()
    static isLoadingPeriods(state: FleetManagementStateModel): boolean {
        return state.isLoadingPeriods;
    }

    @Selector()
    static isLoadingContracts(state: FleetManagementStateModel): boolean {
        return Object.values(state.loadingStates).some(loading => loading);
    }

    @Selector()
    static isLoadingAllContracts(state: FleetManagementStateModel): boolean {
        return state.isLoadingAllContracts;
    }

    @Selector()
    static isLoadingContractsForChangeState(state: FleetManagementStateModel) {
        return (changeState: ChangeState) => state.loadingStates[changeState];
    }

    @Selector()
    static isDownloadingContracts(state: FleetManagementStateModel): boolean {
        return state.isDownloadingContracts;
    }

    @Selector()
    static downloadLink(state: FleetManagementStateModel): string | undefined {
        return state.downloadLink;
    }

    @Selector()
    static selectedPeriod(state: FleetManagementStateModel): Period | undefined {
        return state.selectedPeriod;
    }

    @Action(LoadSelectedPeriod)
    loadSelectedPeriod(
        ctx: StateContext<FleetManagementStateModel>,
        action: LoadSelectedPeriod
    ): Observable<Period | undefined> {
        this.setIsLoadingPeriods(ctx, true);
        this.clearContractState(ctx);
        return this.fleetManagementService
            .getSelectedPeriod(action.payload.accountingUnitId, action.payload.period)
            .pipe(
                tap(period => {
                    ctx.patchState({
                        selectedPeriod: period.items[0]
                    });
                }),
                map(period => (period.items && period.items.length > 0 ? period.items[0] : undefined)),
                finalize(() => this.setIsLoadingPeriods(ctx, false))
            );
    }

    @Action(LoadContracts)
    loadContracts(ctx: StateContext<FleetManagementStateModel>, action: LoadContracts): Observable<Contract[]> {
        const payload = action.payload;
        ctx.patchState({
            loadingStates: {
                ...ctx.getState().loadingStates,
                [payload.changeState]: true
            }
        });
        return this.fleetManagementService
            .getContractsByPeriodId(payload.periodId, payload.changeState, payload.page, payload.pageSize)
            .pipe(
                tap(paginatedContracts => {
                    const contracts = paginatedContracts.items;
                    const stateUpdate: Partial<FleetManagementStateModel> = {};

                    switch (action.payload.changeState) {
                        case ChangeState.KEEP:
                            stateUpdate.keepContracts = contracts;
                            stateUpdate.keepTotalItemCount = paginatedContracts.metadata.itemCount;
                            break;
                        case ChangeState.UPDATE:
                            stateUpdate.updateContracts = contracts;
                            stateUpdate.updateTotalItemCount = paginatedContracts.metadata.itemCount;
                            break;
                        case ChangeState.DELETE:
                            stateUpdate.deleteContracts = contracts;
                            stateUpdate.deleteTotalItemCount = paginatedContracts.metadata.itemCount;
                            break;
                        case ChangeState.CREATE:
                            stateUpdate.createContracts = contracts;
                            stateUpdate.createTotalItemCount = paginatedContracts.metadata.itemCount;
                            break;
                        case ChangeState.INVALID:
                            stateUpdate.invalidContracts = contracts;
                            stateUpdate.invalidTotalItemCount = paginatedContracts.metadata.itemCount;
                            break;
                        default:
                            break;
                    }

                    ctx.patchState(stateUpdate);
                }),
                map(paginatedContracts =>
                    paginatedContracts.items && paginatedContracts.items.length > 0 ? paginatedContracts.items : []
                ),
                finalize(() => {
                    ctx.patchState({
                        loadingStates: {
                            ...ctx.getState().loadingStates,
                            [payload.changeState]: false
                        }
                    });
                })
            );
    }

    @Action(SubmitReview)
    submitReview(ctx: StateContext<FleetManagementStateModel>, action: SubmitReview): Observable<void> {
        this.setIsLoadingPeriods(ctx, true);
        return this.fleetManagementService
            .submitReview(action.payload.periodId, action.payload.review)
            .pipe(finalize(() => this.setIsLoadingPeriods(ctx, false)));
    }

    @Action(LoadBrowseContracts)
    loadBrowseContracts(
        ctx: StateContext<FleetManagementStateModel>,
        action: LoadBrowseContracts
    ): Observable<ContractWithPeriod[]> {
        const payload = action.payload;
        this.setIsLoadingContracts(ctx, true);
        return this.fleetManagementService
            .getAllContracts(
                payload.accountingUnitId,
                payload.isSubmitted,
                payload.startMonth,
                payload.endMonth,
                payload.page,
                payload.pageSize,
                payload.filter,
                payload.sort
            )
            .pipe(
                tap(paginatedContracts => {
                    ctx.patchState({
                        contracts: paginatedContracts.items,
                        totalItemCount: paginatedContracts.metadata.itemCount
                    });
                }),
                map(paginatedContracts =>
                    paginatedContracts.items && paginatedContracts.items.length > 0 ? paginatedContracts.items : []
                ),
                finalize(() => this.setIsLoadingContracts(ctx, false))
            );
    }

    @Action(DownloadBrowseContracts)
    downloadBrowseContracts(
        ctx: StateContext<FleetManagementStateModel>,
        action: DownloadBrowseContracts
    ): Observable<SignedUrl> {
        const payload = action.payload;
        this.setIsDownloadingContracts(ctx, true);
        return this.fleetManagementService
            .downloadAllContracts(
                payload.accountingUnitId,
                payload.isSubmitted,
                payload.startMonth,
                payload.endMonth,
                payload.filter,
                payload.sort
            )
            .pipe(
                tap(signedUrl => ctx.patchState({ downloadLink: signedUrl.signedURL })),
                finalize(() => this.setIsDownloadingContracts(ctx, false))
            );
    }

    @Action(LoadWarningContracts)
    loadWarningContracts(
        ctx: StateContext<FleetManagementStateModel>,
        action: LoadWarningContracts
    ): Observable<Contract[]> {
        const payload = action.payload;
        ctx.patchState({ isLoadingWarningContracts: true });
        return this.fleetManagementService
            .getContractsByPeriodIssues(payload.periodId, payload.page, payload.pageSize, PeriodIssueState.OPEN)
            .pipe(
                tap(paginatedContracts => {
                    ctx.patchState({
                        warningContracts: paginatedContracts.items,
                        warningTotalItemCount: paginatedContracts.metadata.itemCount
                    });
                }),
                map(paginatedContracts =>
                    paginatedContracts.items && paginatedContracts.items.length > 0 ? paginatedContracts.items : []
                ),
                finalize(() => ctx.patchState({ isLoadingWarningContracts: false }))
            );
    }

    @Action(ClearDownloadLink)
    clearDownloadLink(ctx: StateContext<FleetManagementStateModel>): void {
        ctx.patchState({ downloadLink: undefined });
    }

    @Action(DeleteAllPeriods)
    deleteAllPeriods(
        ctx: StateContext<FleetManagementStateModel>,
        action: DeleteAllPeriods
    ): Observable<{ deletedCount: number }> {
        this.setIsLoadingPeriods(ctx, true);
        return this.fleetManagementService.deleteAllPeriodsForAccountingUnit(action.payload.accountingUnitId).pipe(
            tap(() => {
                ctx.patchState({ selectedPeriod: undefined });
                this.clearContractState(ctx);
            }),
            finalize(() => this.setIsLoadingPeriods(ctx, false))
        );
    }

    @Action(MovePeriodBackward)
    movePeriodBackward(ctx: StateContext<FleetManagementStateModel>, action: MovePeriodBackward): Observable<Period> {
        this.setIsLoadingPeriods(ctx, true);
        return this.fleetManagementService.movePeriodBackward(action.payload.periodId, action.payload.newMonth).pipe(
            tap(period => {
                ctx.patchState({ selectedPeriod: period });
            }),
            finalize(() => this.setIsLoadingPeriods(ctx, false))
        );
    }

    private setIsLoadingPeriods(ctx: StateContext<FleetManagementStateModel>, isLoading: boolean): void {
        ctx.patchState({ isLoadingPeriods: isLoading });
    }

    private setIsLoadingContracts(ctx: StateContext<FleetManagementStateModel>, isLoadingAllContracts: boolean): void {
        ctx.patchState({ isLoadingAllContracts });
    }

    private setIsDownloadingContracts(ctx: StateContext<FleetManagementStateModel>, isDownloading: boolean): void {
        ctx.patchState({ isDownloadingContracts: isDownloading });
    }

    private clearContractState(ctx: StateContext<FleetManagementStateModel>): void {
        ctx.patchState({
            contracts: [],
            keepContracts: [],
            updateContracts: [],
            deleteContracts: [],
            createContracts: [],
            invalidContracts: [],
            warningContracts: [],
            totalItemCount: 0,
            keepTotalItemCount: 0,
            updateTotalItemCount: 0,
            deleteTotalItemCount: 0,
            createTotalItemCount: 0,
            invalidTotalItemCount: 0,
            warningTotalItemCount: 0
        });
    }
}
